import { getAnnualInterestRateForCreditScore } from '@nerdwallet/mortgage-utils/rate-adjustment';
import { LOAN_TYPE_CONVENTIONAL } from '@nerdwallet/home-constants';
import { getCostForState, getLoanType } from './lib/helpers';
import runScenario from './lib/run-scenario';
import validateErrorScenarios from './lib/validate-loan';
import { DEFAULT_ANNUAL_INCOME, DEFAULT_CREDIT_SCORE, DEFAULT_HOA, DEFAULT_INTERST_RATE, DEFAULT_LOAN_TERM, DEFAULT_MONTHLY_DEBT, DEFAULT_DTI, DEFAULT_SUGGESTED_PURCHASE_PRICE } from './data/constants';

/**
 * @typedef ItemizedMonthlyPayment
 * @property {number} total  The total monthly housing payment
 * @property {number} mortgage  The principal and interest payment for the calculated mortgage
 * @property {number} taxesAndFees  Property tax, HOA dues and homeowners insurance
 * @property {number} propertyTax  The property tax
 * @property {number} homeownersInsurance  The homeowners insurance payment
 * @property {number} hoa  The homeowners association dues
 * @property {number} mortgageInsurancePayment The mortgage insurance premium payment
 */

/**
 * @typedef ErrorScenario
 * @property {number=} monthlyDebt  The dollar amount of monthly debt the user would need to be able to afford the minimum monthly payment
 * @property {number=} annualIncome  The dollar amount of annual income the user would need to be able to afford the minimum monthly payment
 */

/**
 * @typedef {object} SuggestedHomeValue
 * @property {number} downPayment  The down payment
 * @property {number} downPaymentPercent The down payment percent in decimal
 * @property {boolean} isDownPaymentBelowMin  Is the down payment below the minimum for mortgage insurance (20%)
 * @property {number} fundsNeeded  Funds needed to close the loan; the down payment, closing costs and up front fees
 * @property {number} loanAmount The principal amount of the loan
 * @property {number} suggestedPurchasePrice  The purchasing power or maximum home price
 * @property {number} dti  Debt to income ratio
 * @property {number} loanTerm  The term of the loan in years
 * @property {ItemizedMonthlyPayment} monthlyPayments Itemized monthly payment
 * @property {ItemizedClosingCosts} closingCosts Itemized closing costs
 * @property {MonthlyBudget} monthlyBudget  Itemized monthly budget
 * @property {ErrorScenario=} error  Potential errors, `null` if no error
 */

/**
 * Calculates the suggested/max house value that a user can afford.
 * @module calculate-house-value
 * @param {object} inputs
 * @param {number} inputs.annualIncome Annual income
 * @param {number=} inputs.baseAnnualInterestRate  Annual interest rate if specified and `annualInterestRate` is not, it will adjust based upon user's `state`, `loanTerm`, and `creditScore`
 * @param {number=} [inputs.creditScore=DEFAULT_CREDIT_SCORE]  Credit score as a number
 * @param {number=} [inputs.loanTerm=DEFAULT_LOAN_TERM]  The length of the requested loan in years
 * @param {number=} [inputs.monthlyDebt=DEFAULT_MONTHLY_DEBT]  Monthly debt obligations
 * @param {number=} [inputs.monthlyHoaDues=DEFAULT_HOA]  Monthly homeowners association dues
 * @param {string=} inputs.state  The user's state, used to look up costs that vary across states (e.g. property or transfer taxes)
 * @param {number=} inputs.annualInterestRate  If specified, use this interest rate directly and do not adjust
 * @param {number=} inputs.dti  Overrides the default DTI
 * @param {number=} inputs.downPayment Overrides the calculated down payment
 * @param {number=} inputs.fundsNeeded Overrides the amount of money the user has on hand
 * @param {number=} inputs.monthlyHomeownersInsurance Overrides the monthly homeowners insurance premium
 * @param {number=} inputs.monthlyMortgageInsurance  Overrides the monthly mortgage insurance premium
 * @param {number=} inputs.monthlyPropertyTax  Overrides the monthly property tax payment
 * @param {external:home-constants.VETERAN_TYPE} data.veteranType  The "type" of veteran if eligible for a VA loan
 * @param {boolean} data.firstVaLoan  Is this the user's first va loan?
 * @param {object=} outputs
 * @param {number=} outputs.suggestedPurchasePrice  Purchase price from last iteration of calculation
 * @param {number=} outputs.downPayment  Down payment from last iteration of calculation
 * @returns {SuggestedHomeValue}
 */
export default function calculateSuggestedHouseValue(_ref) {
  var _ref$annualIncome = _ref.annualIncome,
    annualIncome = _ref$annualIncome === void 0 ? DEFAULT_ANNUAL_INCOME : _ref$annualIncome,
    _ref$baseAnnualIntere = _ref.baseAnnualInterestRate,
    baseAnnualInterestRate = _ref$baseAnnualIntere === void 0 ? DEFAULT_INTERST_RATE : _ref$baseAnnualIntere,
    _ref$creditScore = _ref.creditScore,
    creditScore = _ref$creditScore === void 0 ? DEFAULT_CREDIT_SCORE : _ref$creditScore,
    _ref$dti = _ref.dti,
    dti = _ref$dti === void 0 ? DEFAULT_DTI : _ref$dti,
    _ref$loanTerm = _ref.loanTerm,
    loanTerm = _ref$loanTerm === void 0 ? DEFAULT_LOAN_TERM : _ref$loanTerm,
    loanType = _ref.loanType,
    _ref$monthlyDebt = _ref.monthlyDebt,
    monthlyDebt = _ref$monthlyDebt === void 0 ? DEFAULT_MONTHLY_DEBT : _ref$monthlyDebt,
    _ref$monthlyHoaDues = _ref.monthlyHoaDues,
    monthlyHoaDues = _ref$monthlyHoaDues === void 0 ? DEFAULT_HOA : _ref$monthlyHoaDues,
    state = _ref.state,
    veteranType = _ref.veteranType,
    _ref$firstVaLoan = _ref.firstVaLoan,
    firstVaLoan = _ref$firstVaLoan === void 0 ? true : _ref$firstVaLoan,
    _ref$annualInterestRa = _ref.annualInterestRate,
    annualInterestRate = _ref$annualInterestRa === void 0 ? getAnnualInterestRateForCreditScore({
      creditScore: creditScore,
      baseAnnualInterestRate: baseAnnualInterestRate,
      loanTerm: loanTerm,
      state: state
    }) : _ref$annualInterestRa,
    downPayment = _ref.downPayment,
    fundsNeeded = _ref.fundsNeeded,
    _ref$monthlyHomeowner = _ref.monthlyHomeownersInsurance,
    monthlyHomeownersInsurance = _ref$monthlyHomeowner === void 0 ? getCostForState(state, 'homeownersInsurance') / 12 : _ref$monthlyHomeowner,
    monthlyMortgageInsurance = _ref.monthlyMortgageInsurance,
    monthlyPropertyTax = _ref.monthlyPropertyTax;
  var scenarioNoInsurance = runScenario(false, {
    annualIncome: annualIncome,
    creditScore: creditScore,
    dti: dti,
    loanTerm: loanTerm,
    loanType: LOAN_TYPE_CONVENTIONAL,
    monthlyDebt: monthlyDebt,
    monthlyHoaDues: monthlyHoaDues,
    state: state,
    annualInterestRate: annualInterestRate,
    downPayment: downPayment,
    fundsNeeded: fundsNeeded,
    monthlyHomeownersInsurance: monthlyHomeownersInsurance,
    monthlyMortgageInsurance: monthlyMortgageInsurance,
    monthlyPropertyTax: monthlyPropertyTax,
    veteranType: veteranType,
    firstVaLoan: firstVaLoan
  }, {
    suggestedPurchasePrice: DEFAULT_SUGGESTED_PURCHASE_PRICE
  });
  if (!scenarioNoInsurance.isDownPaymentBelowMin) {
    return scenarioNoInsurance;
  }
  var scenarioInsurance = runScenario(true, {
    annualIncome: annualIncome,
    creditScore: creditScore,
    dti: dti,
    loanTerm: loanTerm,
    loanType: loanType || getLoanType({
      creditScore: creditScore,
      veteranType: veteranType
    }),
    monthlyDebt: monthlyDebt,
    monthlyHoaDues: monthlyHoaDues,
    state: state,
    annualInterestRate: annualInterestRate,
    downPayment: downPayment,
    fundsNeeded: fundsNeeded,
    monthlyHomeownersInsurance: monthlyHomeownersInsurance,
    monthlyMortgageInsurance: monthlyMortgageInsurance,
    monthlyPropertyTax: monthlyPropertyTax,
    veteranType: veteranType,
    firstVaLoan: firstVaLoan
  }, {
    suggestedPurchasePrice: DEFAULT_SUGGESTED_PURCHASE_PRICE
  });
  if (scenarioInsurance.dti > dti) {
    var candidate = validateErrorScenarios(true, {
      annualIncome: annualIncome,
      creditScore: creditScore,
      dti: dti,
      loanTerm: loanTerm,
      loanType: loanType || getLoanType({
        creditScore: creditScore,
        veteranType: veteranType
      }),
      monthlyDebt: monthlyDebt,
      monthlyHoaDues: monthlyHoaDues,
      state: state,
      annualInterestRate: annualInterestRate,
      downPayment: downPayment,
      fundsNeeded: fundsNeeded,
      monthlyHomeownersInsurance: monthlyHomeownersInsurance,
      monthlyMortgageInsurance: monthlyMortgageInsurance,
      monthlyPropertyTax: monthlyPropertyTax,
      veteranType: veteranType,
      firstVaLoan: firstVaLoan
    }, {
      suggestedPurchasePrice: DEFAULT_SUGGESTED_PURCHASE_PRICE
    }, function (result) {
      return result.dti <= dti;
    });
    if (candidate) {
      if (candidate.isDownPaymentBelowMin) {
        return candidate;
      }
      var adjustedDtiWithoutInsurance = validateErrorScenarios(false, {
        annualIncome: annualIncome,
        creditScore: creditScore,
        dti: dti,
        loanTerm: loanTerm,
        loanType: loanType || getLoanType({
          creditScore: creditScore,
          veteranType: veteranType
        }),
        monthlyDebt: monthlyDebt,
        monthlyHoaDues: monthlyHoaDues,
        state: state,
        annualInterestRate: annualInterestRate,
        downPayment: downPayment,
        fundsNeeded: fundsNeeded,
        monthlyHomeownersInsurance: monthlyHomeownersInsurance,
        monthlyMortgageInsurance: monthlyMortgageInsurance,
        monthlyPropertyTax: monthlyPropertyTax,
        veteranType: veteranType,
        firstVaLoan: firstVaLoan
      }, {
        suggestedPurchasePrice: DEFAULT_SUGGESTED_PURCHASE_PRICE
      }, function (result) {
        return result.dti <= dti && !result.isDownPaymentBelowMin;
      });
      if (adjustedDtiWithoutInsurance) {
        return adjustedDtiWithoutInsurance;
      }
    }
  }
  return scenarioInsurance;
}